import type { ChatDashboardEntry, ChatDashboardFilters, ChatRoomLock } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

const CHAT_MESSAGES_URL = '/ChatMessages'
const LOCKS_URL = '/Chat/chatrooms'
const SCOPE = 'chatDashboard'

export const chatDashboardKeysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  list: (filters: ChatDashboardFilters, offset: number, limit: number) =>
    [{ scope: SCOPE, filters, offset, limit }] as const,
}

async function getMessageListEntries(
  filters: ChatDashboardFilters,
  offset: number,
  limit: number,
): Promise<ChatDashboardEntry[]> {
  const apiClient = getApiClient()
  const response = await apiClient.post<ChatDashboardEntry[]>(`${CHAT_MESSAGES_URL}/chats`, {
    ...filters,
    offset,
    limit,
  })
  return response.data
}

export function useGetMessageListEntries(
  filters: ChatDashboardFilters | undefined,
  offset: number,
  limit: number,
): [ChatDashboardEntry[], boolean, Error | null] {
  const { data, isFetching, error } = useQuery({
    queryKey: chatDashboardKeysFactory.list(filters!, offset, limit),
    queryFn: () => getMessageListEntries(filters!, offset, limit),
    enabled: !!filters,
    placeholderData: keepPreviousData,
    refetchOnMount: 'always',
  })

  return [data ?? [], isFetching, error]
}

// Locks

const LOCK_SCOPE = 'chatroom-lock'

const lockKeysFactory = {
  batch: (creditApplicationIds: string[]) => [{ scope: LOCK_SCOPE, type: 'batch', creditApplicationIds }] as const,
}

async function getActiveLocks(creditApplicationIds: string[]): Promise<ChatRoomLock[]> {
  const apiClient = getApiClient()
  const response = await apiClient.post<ChatRoomLock[]>(
    `${LOCKS_URL}/locks/batch`,
    creditApplicationIds,
  )
  return response.data
}

export function useGetActiveLocks(
  creditApplicationIds: string[],
): [ChatRoomLock[] | undefined, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: lockKeysFactory.batch(creditApplicationIds),
    queryFn: () => getActiveLocks(creditApplicationIds),
    enabled: creditApplicationIds.length > 0,
  })

  return [data, isFetching]
}

export function useInvalidateDashboard() {
  const queryClient = useQueryClient()
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: chatDashboardKeysFactory.all() })
  }, [queryClient])
}
